import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { getApiErrorMessage, magicFlowApi } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import type {
  MagicFlowOption,
  PublishMagicFlowEventPayload,
  RuleSetRunRecord,
} from './MagicFlowAdmin.types';

type UseMagicFlowDebuggerControllerParams = {
  orgUuid?: string;
  hasOrgSelected: boolean;
  scopedHeaders: Record<string, string | boolean>;
};

const parseRunsResponse = (responseData: any): RuleSetRunRecord[] => {
  if (Array.isArray(responseData?.rule_set_runs))
    return responseData.rule_set_runs;
  if (Array.isArray(responseData?.runs)) return responseData.runs;
  if (Array.isArray(responseData?.items)) return responseData.items;
  if (Array.isArray(responseData?.results)) return responseData.results;
  if (Array.isArray(responseData?.data)) return responseData.data;
  return [];
};

const parseNamespacesResponse = (responseData: any): MagicFlowOption[] => {
  const rawList = Array.isArray(responseData?.namespaces)
    ? responseData.namespaces
    : Array.isArray(responseData?.items)
      ? responseData.items
      : Array.isArray(responseData?.results)
        ? responseData.results
        : Array.isArray(responseData?.data)
          ? responseData.data
          : [];

  return rawList
    .map((item: any) => {
      const name = item?.name || item?.namespace || item?.value;
      if (!name) return null;
      return {
        value: String(name),
        label: String(name),
      } as MagicFlowOption;
    })
    .filter(Boolean) as MagicFlowOption[];
};

export const useMagicFlowDebuggerController = ({
  orgUuid,
  hasOrgSelected,
  scopedHeaders,
}: UseMagicFlowDebuggerControllerParams) => {
  const queryClient = useQueryClient();

  const [namespace, setNamespace] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventId, setEventId] = useState('');
  const [eventDataJson, setEventDataJson] = useState('');

  const [lastPublishResponse, setLastPublishResponse] = useState<any | null>(
    null
  );

  const { data: namespaceOptions = [], isLoading: isLoadingNamespaces } =
    useQuery({
      queryKey: ['magicflow-namespaces', orgUuid],
      enabled: hasOrgSelected,
      queryFn: () =>
        magicFlowApi
          .get('/namespace', {
            headers: scopedHeaders,
            params: {
              page: 1,
              page_size: 200,
              sort_by: 'updated_at',
              sort_direction: 'desc',
            },
          })
          .then((res) => parseNamespacesResponse(res.data)),
    });

  const {
    data: ruleSetRuns = [],
    isLoading: isLoadingRuleSetRuns,
    isFetching: isFetchingRuleSetRuns,
    refetch: refetchRuleSetRuns,
  } = useQuery({
    queryKey: ['magicflow-rule-set-runs', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get('/rule_set_run', {
          headers: scopedHeaders,
          params: {
            page: 1,
            page_size: 100,
            sort_by: 'updated_at',
            sort_direction: 'desc',
          },
        })
        .then((res) => parseRunsResponse(res.data)),
  });

  const publishEventMutation = useMutation({
    mutationFn: (payload: PublishMagicFlowEventPayload) =>
      magicFlowApi.post('/event', payload, { headers: scopedHeaders }),
    onSuccess: (res) => {
      setLastPublishResponse(res.data || null);
      showToastSuccess('Event published successfully');
      setEventId('');
      queryClient.invalidateQueries({
        queryKey: ['magicflow-rule-set-runs', orgUuid],
      });
    },
    onError: (error) => {
      showToastError(
        getApiErrorMessage(error as any) || 'Failed to publish event'
      );
    },
  });

  const hasPublishErrors = useMemo(() => {
    return !namespace.trim() || !eventName.trim() || !eventId.trim();
  }, [eventId, eventName, namespace]);

  const handlePublishEvent = () => {
    if (hasPublishErrors) {
      showToastError('Namespace, event name, and event id are required');
      return;
    }

    let parsedEventData: Record<string, unknown> = {};
    try {
      const parsed = JSON.parse(eventDataJson || '{}');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        parsedEventData = parsed;
      } else {
        showToastError('Event data must be a JSON object');
        return;
      }
    } catch (error) {
      showToastError('Event data must be valid JSON');
      return;
    }

    publishEventMutation.mutate({
      namespace: namespace.trim(),
      event_name: eventName.trim(),
      event_id: eventId.trim(),
      event_data: parsedEventData,
    });
  };

  return {
    namespace,
    setNamespace,
    eventName,
    setEventName,
    eventId,
    setEventId,
    eventDataJson,
    setEventDataJson,
    namespaceOptions,
    isLoadingNamespaces,
    hasPublishErrors,
    isPublishingEvent: publishEventMutation.isPending,
    handlePublishEvent,
    ruleSetRuns,
    isLoadingRuleSetRuns,
    isFetchingRuleSetRuns,
    refreshRuleSetRuns: () => refetchRuleSetRuns(),
    lastPublishResponse,
  };
};
